import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Check } from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/boards", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-white text-zinc-950 flex flex-col">
      {/* Nav - mobile-first */}
      <header className="h-14 sm:h-16 border-b border-zinc-200 px-4 sm:px-12 flex items-center justify-between sticky top-0 bg-white z-10">
        <Link to="/" className="font-display text-lg sm:text-xl font-black tracking-tighter" data-testid="landing-logo">
          KANBN<span className="text-red-500">.</span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Link
            to="/login"
            className="px-3 sm:px-4 py-2 text-sm font-medium hover:text-zinc-500 transition-colors"
            data-testid="landing-login"
          >
            Login
          </Link>
          <Link
            to="/register"
            className="px-4 sm:px-5 py-2 text-sm font-medium bg-black text-white hover:bg-zinc-800 transition-colors"
            data-testid="landing-signup"
          >
            Start
          </Link>
        </div>
      </header>

      {/* Hero - mobile-first stack, then 12-col grid on lg */}
      <section className="flex-1 grid grid-cols-1 lg:grid-cols-12 border-b border-zinc-200">
        <div className="lg:col-span-7 px-4 sm:px-12 py-10 sm:py-16 lg:py-24 flex flex-col justify-center lg:border-r border-zinc-200">
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <span className="h-px w-8 sm:w-12 bg-zinc-900" />
            <span className="font-mono-tight text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500">
              v01 / Kanban for builders
            </span>
          </div>
          <h1
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-zinc-950 max-w-3xl"
            data-testid="landing-headline"
          >
            Boards without
            <br />
            the busywork.
          </h1>
          <p className="mt-6 sm:mt-8 max-w-xl text-base sm:text-lg text-zinc-500 leading-relaxed">
            A focused, opinionated kanban tool. Lists, cards, drag &amp; drop, due dates,
            checklists, comments, members. No bloat, no purple gradients.
          </p>
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              className="group inline-flex items-center justify-center gap-2 bg-black text-white px-7 py-4 text-sm font-medium hover:bg-zinc-800 transition-colors"
              data-testid="landing-cta-primary"
            >
              Start building free
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 border border-zinc-200 px-7 py-4 text-sm font-medium hover:border-black transition-colors"
              data-testid="landing-cta-secondary"
            >
              I have an account
            </Link>
          </div>

          <div className="mt-12 sm:mt-16 grid grid-cols-3 gap-4 sm:gap-6 max-w-2xl">
            {[
              { k: "100%", v: "Keyboard friendly" },
              { k: "0", v: "Tracking pixels" },
              { k: "∞", v: "Boards per account" },
            ].map((s) => (
              <div key={s.v} className="border-l border-zinc-200 pl-3 sm:pl-4">
                <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter">{s.k}</div>
                <div className="text-[10px] sm:text-xs text-zinc-500 mt-1 uppercase tracking-wider">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative grain bg-zinc-50 min-h-[280px] sm:min-h-[400px] order-first lg:order-last border-b lg:border-b-0 border-zinc-200">
          <div className="absolute inset-0 p-4 sm:p-8 flex flex-col justify-end">
            <div className="border border-zinc-200 bg-white p-3 sm:p-6 max-w-md">
              <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                Board / acquisition
              </div>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {["backlog", "active", "shipped"].map((c, i) => (
                  <div key={c} className="border border-zinc-200 p-1.5 sm:p-2">
                    <div className="font-mono-tight text-[8px] sm:text-[9px] uppercase tracking-wider text-zinc-500 mb-2">
                      {c}
                    </div>
                    <div className="space-y-1.5">
                      <div className="border border-zinc-200 p-1 sm:p-1.5 text-[9px] sm:text-[10px]">Q3 OKRs</div>
                      {i !== 0 && <div className="border border-zinc-200 p-1 sm:p-1.5 text-[9px] sm:text-[10px]">Hire 2 eng</div>}
                      {i === 2 && <div className="border border-zinc-200 p-1 sm:p-1.5 text-[9px] sm:text-[10px]">Brand v2</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-4 sm:px-12 py-12 sm:py-20 grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 max-w-7xl mx-auto w-full">
        {[
          { t: "Lists & cards", d: "Multiple columns, drag-and-drop cards, instant updates." },
          { t: "Due dates & checklists", d: "Calendar picker, labels, sub-tasks per card." },
          { t: "Collaborate", d: "Invite teammates by email, share comments and an activity log." },
        ].map((f) => (
          <div key={f.t} className="border-t border-zinc-900 pt-5 sm:pt-6">
            <div className="font-mono-tight text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
              feature
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-3">{f.t}</h3>
            <p className="text-sm text-zinc-500 leading-relaxed">{f.d}</p>
            <ul className="mt-4 sm:mt-6 space-y-2">
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-zinc-900 shrink-0" />
                  <span className="text-zinc-700">Included in every board</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <footer className="px-4 sm:px-12 py-6 sm:py-8 border-t border-zinc-200 flex items-center justify-between text-[10px] sm:text-xs text-zinc-500">
        <div>© 2026 KANBN. Built for focus.</div>
        <div className="font-mono-tight uppercase tracking-widest">v1.0</div>
      </footer>
    </div>
  );
}
